import {v4 as uuidv4} from 'uuid'
import type {ChatMessage, ProgressStepData, ProgressStepUpdateData} from './types'

export type RestoredChatMessage = Pick<
  ChatMessage,
  'role' | 'content' | 'attachments' | 'websiteTargetReferences' | 'handoff' | 'progressSteps' | 'siteBuilderPlan' | 'failed'
> & {agent?: string}

export const PROVISIONAL_PLANNING_STEP_ID = 'agent:pending:thinking'

/**
 * Ros owns the turn as soon as the user sends it, before routing/model/prompt preflight can emit
 * backend SSE. Keep the rich activity surface truthful during that gap instead of falling back to
 * the legacy "Thinking..." placeholder. The first server-issued activity replaces this row.
 */
export function createProvisionalPlanningStep(startedAt = Date.now()): ProgressStepData {
  return {
    stepId: PROVISIONAL_PLANNING_STEP_ID,
    status: 'running',
    name: 'Planning next steps',
    startedAt,
    category: 'agent',
  }
}

export function replaceProvisionalPlanningStep(
  steps: ProgressStepData[],
  serverStep: ProgressStepData,
): ProgressStepData[] {
  const withoutProvisional = steps.filter(step => step.stepId !== PROVISIONAL_PLANNING_STEP_ID)
  const existing = withoutProvisional.findIndex(step => step.stepId === serverStep.stepId)
  if (existing >= 0) withoutProvisional[existing] = serverStep
  else withoutProvisional.push(serverStep)
  return withoutProvisional
}

export function mergeProgressStepUpdate(step: ProgressStepData, data: ProgressStepUpdateData): ProgressStepData {
  return {
    ...step,
    ...(data.status !== undefined ? {status: data.status as ProgressStepData['status']} : {}),
    ...(data.timing !== undefined ? {timing: data.timing} : {}),
    ...(data.startedAt !== undefined ? {startedAt: data.startedAt} : {}),
    ...(data.completedAt !== undefined ? {completedAt: data.completedAt} : {}),
    ...(data.category !== undefined ? {category: data.category} : {}),
    ...(data.details !== undefined ? {details: data.details} : {}),
    ...(data.substeps !== undefined ? {substeps: data.substeps} : {}),
  }
}

export function finalizeRunningProgressSteps(
  steps: ProgressStepData[] | undefined,
  status: 'complete' | 'warning' | 'failed',
  completedAt = Date.now(),
): ProgressStepData[] | undefined {
  return steps?.map(step => step.status === 'running'
    ? {...step, status, completedAt}
    : step)
}

/**
 * Append streamed text without changing the turn lifecycle.
 *
 * A chunk is payload, not a "turn started" signal. In particular, a delayed chunk must not
 * resurrect a message after the terminal `complete` event has already marked it finished.
 * The owning send/resume path is solely responsible for setting `isStreaming: true`.
 */
export function appendAssistantChunk(message: ChatMessage, chunk: string): ChatMessage {
  return {...message, content: message.content + chunk}
}

export function restoreChatMessages(history: RestoredChatMessage[]): ChatMessage[] {
  return history
    .filter(m => m.content?.trim() || m.handoff || m.attachments?.length || m.websiteTargetReferences?.length || m.progressSteps?.length || m.siteBuilderPlan)
    .map(m => ({
      id: uuidv4(),
      role: m.role,
      content: m.content || '',
      ...(m.attachments?.length ? {attachments: m.attachments} : {}),
      ...(m.websiteTargetReferences?.length ? {websiteTargetReferences: m.websiteTargetReferences} : {}),
      ...(m.agent ? { intent: { intent: m.agent, confidence: 1, reasoning: 'restored' } } : {}),
      ...(m.handoff ? { handoff: m.handoff } : {}),
      ...(m.progressSteps?.length ? { progressSteps: m.progressSteps } : {}),
      ...(m.siteBuilderPlan ? {siteBuilderPlan: m.siteBuilderPlan} : {}),
      ...(m.failed ? { failed: true } : {}),
    }))
}
